import React, { useState, useMemo, useCallback, useRef, useEffect } from 'react';
import { debounce } from 'lodash';
import { useStore } from '../../store';
import { STATUS, PRIORITY, TASK_TYPE, fmt } from '../../utils';
import { AvatarGroup } from '../UI/Avatar';
import { ChevronDown, ChevronRight, Plus, ArrowUpDown, Check } from 'lucide-react';
import CreateTaskModal from './CreateTaskModal';
import { usePermissions } from '../../hooks/usePermissions';

export default React.memo(function TaskListView() {
  const { tasks, openTask, updateTask, activeProject } = useStore();
  const { canCreateTask } = usePermissions();
  const canCreate = canCreateTask(activeProject);
  const [selectedStatuses, setSelectedStatuses] = useState(() => Object.keys(STATUS));
  const [priorityFilter, setPriorityFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [sortConfig, setSortConfig] = useState({ key: null, dir: 'asc' });
  const [statusDropdownOpen, setStatusDropdownOpen] = useState(false);
  const statusDropdownRef = useRef(null);

  useEffect(() => {
    const handler = e => {
      if (statusDropdownRef.current && !statusDropdownRef.current.contains(e.target)) {
        setStatusDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const debouncedSetSearch = useCallback(debounce(setSearch, 300), []);

  const filtered = useMemo(() => tasks.filter(t => {
    if (t.parent) return false;
    if (!selectedStatuses.includes(t.status)) return false;
    if (priorityFilter !== 'all' && t.priority !== priorityFilter) return false;
    if (search && !t.title.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  }), [tasks, selectedStatuses, priorityFilter, search]);

  const sorted = useMemo(() => {
    if (!sortConfig.key) return filtered;
    const list = [...filtered];
    const statusOrder = Object.keys(STATUS);
    const priorityOrder = Object.keys(PRIORITY);
    list.sort((a, b) => {
      let cmp = 0;
      if (sortConfig.key === 'status') {
        cmp = statusOrder.indexOf(a.status) - statusOrder.indexOf(b.status);
      } else if (sortConfig.key === 'priority') {
        cmp = priorityOrder.indexOf(a.priority) - priorityOrder.indexOf(b.priority);
      }
      return sortConfig.dir === 'asc' ? cmp : -cmp;
    });
    return list;
  }, [filtered, sortConfig]);

  const toggleSort = useCallback((key) => {
    setSortConfig(prev => {
      if (prev.key === key) {
        if (prev.dir === 'asc') return { key, dir: 'desc' };
        return { key: null, dir: 'asc' };
      }
      return { key, dir: 'asc' };
    });
  }, []);

  const grouped = useMemo(() => {
    const g = {};
    filtered.forEach(t => {
      const k = t.status;
      if (!g[k]) g[k] = [];
      g[k].push(t);
    });
    return g;
  }, [filtered]);

  return (
    <div className="h-full overflow-y-auto p-4">
      {/* Filters */}
      <div className="flex items-center gap-2 mb-4 flex-wrap">
        <input className="input w-56 text-sm" placeholder="Поиск задач..." defaultValue={search} onChange={e => debouncedSetSearch(e.target.value)} />
        <div className="relative inline-block" ref={statusDropdownRef}>
          <button
            type="button"
            onClick={() => setStatusDropdownOpen(o => !o)}
            className="input w-auto text-sm inline-flex items-center gap-1"
          >
            {selectedStatuses.length === Object.keys(STATUS).length ? 'Все статусы' : `Статусы (${selectedStatuses.length})`}
            <ChevronDown className="w-3 h-3" />
          </button>
          {statusDropdownOpen && (
            <div className="absolute z-50 mt-1 w-56 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl shadow-xl animate-scale-in">
              <div className="p-1.5">
                {Object.entries(STATUS).map(([k, v]) => {
                  const checked = selectedStatuses.includes(k);
                  return (
                    <button
                      key={k}
                      type="button"
                      onClick={() => setSelectedStatuses(prev => checked ? prev.filter(s => s !== k) : [...prev, k])}
                      className={`flex items-center gap-2 w-full px-2 py-1.5 rounded-lg text-left text-sm transition-colors ${checked ? 'bg-brand-50 dark:bg-brand-900/30' : 'hover:bg-gray-50 dark:hover:bg-gray-800'}`}
                    >
                      <span className="w-2 h-2 rounded-full" style={{ background: v.color }} />
                      <span className="flex-1">{v.label}</span>
                      {checked && <Check className="w-3.5 h-3.5 text-brand-600 flex-shrink-0" />}
                    </button>
                  );
                })}
                {selectedStatuses.length !== Object.keys(STATUS).length && (
                  <>
                    <div className="border-t border-gray-100 dark:border-gray-800 my-1" />
                    <button
                      type="button"
                      onClick={() => setSelectedStatuses(Object.keys(STATUS))}
                      className="flex items-center gap-2 w-full px-2 py-1.5 rounded-lg text-xs text-gray-500 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
                    >
                      Выбрать все
                    </button>
                  </>
                )}
              </div>
            </div>
          )}
        </div>
        <select className="input w-auto text-sm" value={priorityFilter} onChange={e => setPriorityFilter(e.target.value)}>
          <option value="all">Все приоритеты</option>
          {Object.entries(PRIORITY).map(([k, v]) => <option key={k} value={k}>{v.icon} {v.label}</option>)}
        </select>

        <div className="flex-1" />
        {canCreate && (
          <button onClick={() => setShowCreate(true)} className="btn-primary py-1.5"><Plus className="w-4 h-4" /> Задача</button>
        )}
        {!canCreate && (
          <button disabled title="Требуется роль участника или выше" className="btn-primary py-1.5 opacity-50"><Plus className="w-4 h-4" /> Задача</button>
        )}
      </div>

      {/* Table */}
      <div className="card overflow-x-scroll">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-gray-100 dark:border-gray-800">
              <th className="text-left px-4 py-2.5 text-xs font-medium text-gray-500 dark:text-gray-400">Задача</th>
              <th onClick={() => toggleSort('status')} className="text-left px-3 py-2.5 text-xs font-medium text-gray-500 dark:text-gray-400 w-28 cursor-pointer select-none hover:text-gray-700 dark:hover:text-gray-200">
                <div className="flex items-center gap-1">
                  Статус
                  {sortConfig.key === 'status' ? (
                    <ChevronDown className={`w-3 h-3 transition-transform ${sortConfig.dir === 'asc' ? 'rotate-180' : ''}`} />
                  ) : (
                    <ArrowUpDown className="w-3 h-3 opacity-40" />
                  )}
                </div>
              </th>
              <th onClick={() => toggleSort('priority')} className="text-left px-3 py-2.5 text-xs font-medium text-gray-500 dark:text-gray-400 w-24 cursor-pointer select-none hover:text-gray-700 dark:hover:text-gray-200">
                <div className="flex items-center gap-1">
                  Приоритет
                  {sortConfig.key === 'priority' ? (
                    <ChevronDown className={`w-3 h-3 transition-transform ${sortConfig.dir === 'asc' ? 'rotate-180' : ''}`} />
                  ) : (
                    <ArrowUpDown className="w-3 h-3 opacity-40" />
                  )}
                </div>
              </th>
              <th className="text-left px-3 py-2.5 text-xs font-medium text-gray-500 dark:text-gray-400 w-28">Срок</th>
              <th className="text-left px-3 py-2.5 text-xs font-medium text-gray-500 dark:text-gray-400 w-20">Исполнители</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-50 dark:divide-gray-800/50">
            {filtered.length === 0 && (
              <tr><td colSpan={5} className="text-center py-10 text-gray-400 text-sm">Задач не найдено</td></tr>
            )}
            {sorted.map(task => {
              const s = STATUS[task.status];
              const p = PRIORITY[task.priority];
              const due = fmt.dueBadge(task.dueDate);
              return (
                <React.Fragment key={task._id}>
                  <tr onClick={() => openTask(task._id)}
                    className="hover:bg-gray-50 dark:hover:bg-gray-800/50 cursor-pointer transition-colors">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <span className="text-xs">{TASK_TYPE[task.type]?.icon}</span>
                        <span className="font-medium text-gray-900 dark:text-gray-100 hover:text-brand-600 dark:hover:text-brand-400 transition-colors">{task.title}</span>
                        {task.number && <span className="text-xs text-gray-400 font-mono">#{task.number}</span>}
                      </div>
                      {task.tags?.length > 0 && (
                        <div className="flex gap-1 mt-1 ml-5">
                          {task.tags.slice(0, 3).map(tag => (
                            <span key={tag} className="px-1.5 py-0.5 rounded text-[10px] bg-brand-50 dark:bg-brand-900/30 text-brand-600 dark:text-brand-400">{tag}</span>
                          ))}
                        </div>
                      )}
                    </td>
                    <td className="px-3 py-3">
                      <span className="tag" style={{ background: s?.color + '20', color: s?.color }}>
                        <span className="w-1.5 h-1.5 rounded-full" style={{ background: s?.color }} />
                        {s?.label}
                      </span>
                    </td>
                    <td className="px-3 py-3">
                      <span className="text-sm" title={p?.label}>{p?.icon} <span className="text-xs text-gray-500">{p?.label}</span></span>
                    </td>
                    <td className="px-3 py-3">
                      {due && <span className={`tag text-[11px] ${due.cls}`}>{due.label}</span>}
                    </td>
                    <td className="px-3 py-3">
                      {task.assignees?.length > 0 && <AvatarGroup users={task.assignees} max={2} />}
                    </td>
                  </tr>
                  {task.checklist?.length > 0 && task.checklist.map((item, i) => (
                    <tr key={`${task._id}-check-${i}`} className="bg-gray-50 dark:bg-gray-900/30">
                      <td className="px-4 py-1 text-xs text-gray-500 dark:text-gray-400" colSpan={2}>
                        <label className="inline-flex items-center gap-1">
                          <input type="checkbox" checked={item.done} onChange={async (e) => {
                            e.stopPropagation();
                            const checklist = task.checklist.map((c, idx) => idx === i ? { ...c, done: !c.done } : c);
                            await updateTask(task._id, { checklist });
                          }} className="rounded-checkbox" style={{ '--checkbox-accent': activeProject?.color || '#4f63ff', '--checkbox-border': activeProject?.color || '#4f63ff' }} />
                          <span className={item.done ? 'line-through text-gray-400' : ''}>{item.text}</span>
                        </label>
                      </td>
                      <td colSpan={3}></td>
                    </tr>
                  ))}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>

      {showCreate && <CreateTaskModal onClose={() => setShowCreate(false)} />}
    </div>
  );
});
